import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { BookOpenIcon, CheckCircleIcon, PlusIcon, MagnifyingGlassIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { standardsAPI } from '../services/standardsApi';
import { useAuth } from '../contexts/AuthContext';
import CreateStandardModal from '../components/CreateStandardModal';
import toast from 'react-hot-toast';
import PaginationBar from '../components/PaginationBar';
import { getApiErrorMessage } from '../utils/apiError';

// Copy of the main app's client/src/pages/StandardsPage.js (plan 18.3), trimmed: no
// Linkages link/route exists in this demo (18.3's bullet lists only Import Standard and
// Create Standard as the admin-only header actions).
export default function StandardsPage() {
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const isAdmin = user?.role === 'ADMIN';

  // Debounce search so typing does not refetch / remount on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data: standardsData, isLoading } = useQuery(
    ['standards', page, pageSize, debouncedSearch],
    () => standardsAPI.getAll({
      page,
      // page_size, not limit. The backend declares page/page_size (18.2); sending "limit"
      // was silently ignored, so the [25, 50, 100] selector did nothing and paging past
      // page 1 at any size other than the server default of 50 skipped rows.
      page_size: pageSize,
      search: debouncedSearch || undefined,
    }),
    {
      select: (data) => data.data,
      keepPreviousData: true,
    }
  );

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, pageSize]);

  // Create standard mutation
  const createStandardMutation = useMutation(
    (standardData) => standardsAPI.create(standardData),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('standards');
        setShowCreateModal(false);
        toast.success('Standard created successfully!');

        // Navigate to the newly created standard
        if (response.data?.id) {
          navigate(`/standards/${response.data.id}`);
        }
      },
      onError: (error) => {
        toast.error(getApiErrorMessage(error, 'Failed to create standard'));
      }
    }
  );

  const standards = standardsData?.standards || [];
  const totalStandards = standardsData?.total ?? standards.length;
  const filteredStandards = standards;

  const handleRowClick = (standard) => {
    navigate(`/standards/${standard.id}`);
  };

  // Only full-page spinner on initial load — keep search mounted while refetching
  if (isLoading && !standardsData) {
    return (
      <div className="min-h-screen bg-[#F8F8FF]">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F8FF]">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Certification Standards</h1>
              <p className="text-gray-600">Browse available certification standards and their requirements.</p>
            </div>
            {isAdmin && (
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => navigate('/admin/standards/import')}
                  className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                >
                  <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
                  Import Standard
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Create Standard
                </button>
              </div>
            )}
          </div>
        </div>

        {!isLoading && totalStandards === 0 && !searchTerm && !debouncedSearch ? (
          /* Empty state */
          <div className="text-center mt-12">
            <BookOpenIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No standards available</h3>
            <p className="mt-1 text-sm text-gray-500">
              {isAdmin
                ? 'Get started by creating your first certification standard.'
                : 'Standards will be loaded from the database.'
              }
            </p>
            {isAdmin && (
              <div className="mt-6 flex justify-center space-x-3">
                <button
                  type="button"
                  onClick={() => navigate('/admin/standards/import')}
                  className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                >
                  <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
                  Import Standard
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Create Standard
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Search Bar */}
            <div className="mb-6">
              <div className="max-w-md relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Search standards..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              {searchTerm && (
                <p className="mt-2 text-sm text-gray-600">
                  {totalStandards} {totalStandards === 1 ? 'standard' : 'standards'} match "{searchTerm}"
                </p>
              )}
            </div>

            {/* Standards Table */}
            {filteredStandards.length > 0 ? (
              <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-[#F8F8FF]">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Standard
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Version
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Requirements
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredStandards.map((standard) => (
                      <tr
                        key={standard.id}
                        className="hover:bg-[#F8F8FF] cursor-pointer"
                        onClick={() => handleRowClick(standard)}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <BookOpenIcon className="h-5 w-5 text-primary-500 mr-3 flex-shrink-0" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {standard.name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {standard.full_name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {standard.version}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            standard.is_active
                              ? 'bg-accent-100 text-accent-800'
                              : 'bg-[#F8F8FF] text-gray-800'
                          }`}>
                            {standard.is_active ? (
                              <>
                                <CheckCircleIcon className="h-3 w-3 mr-1" />
                                Active
                              </>
                            ) : (
                              'Inactive'
                            )}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {standard.requirements_count || 0} requirements
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          <div className="max-w-xs truncate">
                            {standard.description}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              /* No search results */
              <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-6">
                <div className="text-center py-8">
                  <MagnifyingGlassIcon className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                  <h3 className="text-sm font-medium text-gray-900">No standards found</h3>
                  <p className="text-sm text-gray-500">
                    No standards match your search term "{searchTerm}".
                  </p>
                  <button
                    onClick={() => setSearchTerm('')}
                    className="mt-3 text-primary-600 hover:text-primary-500 text-sm font-medium"
                  >
                    Clear search
                  </button>
                </div>
              </div>
            )}

            {totalStandards > 0 && (
              <PaginationBar
                className="mt-6 bg-white rounded-lg border border-gray-200 px-6 py-4"
                page={page}
                pageSize={pageSize}
                total={totalStandards}
                onPageChange={setPage}
                onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                pageSizeOptions={[25, 50, 100]}
                itemLabel={searchTerm ? 'standards (filtered)' : 'standards'}
              />
            )}
          </>
        )}

        {/* Create Standard Modal */}
        <CreateStandardModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={(data) => createStandardMutation.mutate(data)}
          isSubmitting={createStandardMutation.isLoading}
        />
      </div>
    </div>
  );
}
